import { ExtractionResult, ExtractionResponse, ICD10Code, PcsExtractionResponse } from '../types/icd10';
import { ExtractionSession, ExtractionStatus } from '../types/multi-extraction';
import type { CodeMetadataRequest, CodeMetadataResponse } from '../types';

// API configuration
const API_BASE = '/api';

const normalizePcsPayload = (
  payload: unknown,
): PcsExtractionResponse | null => {
  if (!payload) return null;
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload) as PcsExtractionResponse;
    } catch (error) {
      console.warn('Failed to parse PCS payload string:', error);
      return null;
    }
  }
  if (typeof payload === 'object') {
    return payload as PcsExtractionResponse;
  }
  return null;
};

const coerceIcd10Codes = (value: unknown): ICD10Code[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (!entry) return null;

        if (typeof entry === 'string') {
          return { code: entry } as ICD10Code;
        }

        if (typeof entry === 'object' && 'code' in entry) {
          return entry as ICD10Code;
        }

        return null;
      })
      .filter((code): code is ICD10Code => Boolean(code));
  }

  if (typeof value === 'string') {
    try {
      return coerceIcd10Codes(JSON.parse(value));
    } catch (error) {
      console.warn('Failed to parse codes string:', error);
      return [];
    }
  }

  if (typeof value === 'object' && value !== null) {
    const maybeCodes = (value as { codes?: unknown }).codes;
    if (maybeCodes) {
      return coerceIcd10Codes(maybeCodes);
    }
  }

  return [];
};

export interface ExtractionApiError extends Error {
  status?: number;
  code?: string;
  rawBody?: string;
  details?: unknown;
}

export class ExtractionApiService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('bearer_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let detailPayload: any = null;
      let rawBody: string | undefined;
      try {
        rawBody = await response.text();
        if (rawBody && rawBody.trim()) {
          detailPayload = JSON.parse(rawBody);
        }
      } catch (parseError) {
        console.warn('Failed to parse error response body:', parseError);
      }

      const error = new Error(`API request failed: ${response.statusText}`) as ExtractionApiError;
      error.status = response.status;
      error.details = detailPayload ?? rawBody;

      const detail =
        detailPayload?.detail && typeof detailPayload.detail === 'object'
          ? detailPayload.detail
          : detailPayload;

      const raiseApiMessage =
        detail?.user_message
        || detail?.error
        || detail?.detail;

      if (response.status === 401 || response.status === 403) {
        error.code = 'UNAUTHORIZED';
        error.message = raiseApiMessage || 'Authentication required';
      } else if (response.status >= 500) {
        error.code = 'SERVER_ERROR';
        error.message = raiseApiMessage || 'Server error occurred';
      } else {
        error.code = 'CLIENT_ERROR';
        if (raiseApiMessage) {
          error.message = String(raiseApiMessage);
        }
      }

      throw error;
    }

    if (response.status === 204 || response.status === 205) {
      return undefined as unknown as T;
    }

    const rawBody = await response.text();

    if (!rawBody || !rawBody.trim()) {
      return undefined as unknown as T;
    }

    try {
      return JSON.parse(rawBody) as T;
    } catch (parseError) {
      const error = new Error('Failed to parse JSON response') as ExtractionApiError;
      error.status = response.status;
      error.code = 'INVALID_JSON';
      error.rawBody = rawBody;
      error.details = parseError;
      console.error('Failed to parse JSON response:', parseError, rawBody);
      throw error;
    }
  }

  /**
   * Fetch all saved extractions for the authenticated user
   */
  async fetchSavedExtractions(): Promise<ExtractionResult[]> {
    try {
      const response = await fetch(`${API_BASE}/extractions`, {
        credentials: 'include',
        headers: this.getAuthHeaders(),
      });

      const status = response.status;
      const data = await this.handleResponse<any>(response);

      if (data == null) {
        console.warn('fetchSavedExtractions: empty response body', { status });
        return [];
      }

      if (typeof data === 'object' && 'error' in data && data.error) {
        const backendError = new Error(
          `Backend error while loading extractions: ${String((data as any).error)}`
        ) as ExtractionApiError;
        backendError.status = status;
        backendError.code = 'BACKEND_ERROR';
        backendError.details = data;
        console.error('fetchSavedExtractions received backend error payload:', data);
        throw backendError;
      }

      let rows: any[];
      if (Array.isArray(data)) {
        rows = data;
      } else if (data && Array.isArray((data as any).extractions)) {
        rows = (data as any).extractions;
      } else {
        const error = new Error('Unexpected response shape when loading extractions') as ExtractionApiError;
        error.status = status;
        error.code = 'INVALID_RESPONSE';
        error.details = data;
        console.error('fetchSavedExtractions received unexpected payload:', data);
        throw error;
      }

      return rows.map((row: any) => {
        let parsedExtractionJson: Record<string, any> | null = null;
        let codes: ICD10Code[] = [];

        if (row.extraction_json) {
          if (typeof row.extraction_json === 'string') {
            try {
              parsedExtractionJson = JSON.parse(row.extraction_json);
            } catch (e) {
              console.warn('Failed to parse extraction_json string:', e);
              parsedExtractionJson = null;
            }
          } else if (typeof row.extraction_json === 'object') {
            parsedExtractionJson = row.extraction_json;
          }
        }

        const parsedCodes = [
          coerceIcd10Codes(row.codes),
          coerceIcd10Codes(parsedExtractionJson?.codes),
          coerceIcd10Codes(parsedExtractionJson),
        ].find((candidate) => candidate.length > 0);

        codes = parsedCodes ?? [];

        let extraJson: Record<string, any> | null = null;
        if (row.extra_json) {
          if (typeof row.extra_json === 'string') {
            try {
              extraJson = JSON.parse(row.extra_json);
            } catch (e) {
              console.warn('Failed to parse extra_json string:', e);
              extraJson = null;
            }
          } else if (typeof row.extra_json === 'object') {
            extraJson = row.extra_json;
          }
        }

        const pcsPayload = normalizePcsPayload(row.pcs)
          || normalizePcsPayload(parsedExtractionJson?.pcs)
          || normalizePcsPayload(extraJson?.pcs)
          || null;

        return {
          request_id: row.request_id,
          input_text: row.input_text,
          codes,
          user_feedback: row.user_feedback || row.feedback,
          created_at: row.created_at,
          duration_seconds: row.duration_seconds || row.duration || 0,
          extra_json: extraJson,
          extraction_json: parsedExtractionJson,
          pcs: pcsPayload,
        };
      });
    } catch (error) {
      console.error('Error in fetchSavedExtractions:', error);
      throw error; // Let the caller handle authentication errors
    }
  }

  /**
   * Delete a saved extraction
   */
  async deleteSavedExtraction(requestId: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/extraction/${encodeURIComponent(requestId)}`,
      {
        method: 'DELETE',
        credentials: 'include',
        headers: this.getAuthHeaders(),
      }
    );

    await this.handleResponse(response);
  }

  /**
   * Update feedback for a saved extraction
   */
  async updateExtractionFeedback(requestId: string, feedback: string): Promise<void> {
    const response = await fetch(
      `${API_BASE}/extractions/${encodeURIComponent(requestId)}/feedback`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          ...this.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ feedback }),
      }
    );

    await this.handleResponse(response);
  }

  /**
   * Fetch metadata for ICD-10/PCS codes
   */
  async fetchCodesMetadata(payload: CodeMetadataRequest): Promise<CodeMetadataResponse> {
    const response = await fetch(`${API_BASE}/codes/metadata`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...this.getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    return this.handleResponse<CodeMetadataResponse>(response);
  }
}

// Singleton instance
export const extractionApi = new ExtractionApiService();

// Utility functions for data transformation
export class ExtractionDataTransformer {
  /**
   * Generate stable HSL colors for ICD-10 codes
   */
  static generateCodeColors(codes: Array<{ code: string }>): Record<string, string> {
    const colors: Record<string, string> = {};
    
    codes.forEach(({ code }) => {
      const hue = this.hashToHue(code);
      const saturation = 65;
      const lightness = 72;
      colors[code] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    });
    
    return colors;
  }

  /**
   * Generate stable hue from string hash
   */
  private static hashToHue(s: string): number {
    let hash = 0;
    for (let i = 0; i < s.length; i++) {
      hash = (hash << 5) - hash + s.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash) % 360;
  }

  /**
   * Convert backend extraction result to frontend session format
   */
  static savedExtractionToSession(extraction: ExtractionResult): ExtractionSession {
    console.log('Converting extraction to session:', {
      request_id: extraction.request_id,
      created_at: extraction.created_at,
      duration_seconds: extraction.duration_seconds
    });

    // Use the created_at timestamp for start/end times
    let createdAt: Date;
    if (extraction.created_at) {
      // Try to parse the date, handle various formats
      const dateValue = extraction.created_at as any;
      console.log('Parsing created_at:', dateValue);
      
      // If it's already a Date object
      if (dateValue instanceof Date) {
        createdAt = dateValue;
      } else if (typeof dateValue === 'string') {
        // Handle different date formats
        if (dateValue.includes('T') || dateValue.includes('Z') || /[+-]\d{2}:?\d{2}$/.test(dateValue)) {
          createdAt = new Date(dateValue);
        } else {
          // Assume it's in YYYY-MM-DD HH:MM:SS format, convert to ISO
          const isoStr = dateValue.replace(' ', 'T') + 'Z';
          createdAt = new Date(isoStr);
        }
      } else if (typeof dateValue === 'number') {
        // Heuristic: if value looks like seconds (10 digits) convert to ms
        // Anything less than Jan 2002 in ms (approx 1000000000000) treat as seconds
        const num = dateValue;
        const ms = num < 1000000000000 ? num * 1000 : num; // < 1e12 => seconds
        createdAt = new Date(ms);
      } else {
        createdAt = new Date();
      }
    } else {
      createdAt = new Date();
    }

    // Fallback if invalid date
    if (isNaN(createdAt.getTime())) {
      createdAt = new Date();
    }

    console.log('Parsed createdAt:', createdAt.toISOString());
    const endTime = extraction.duration_seconds
      ? new Date(createdAt.getTime() + (extraction.duration_seconds * 1000))
      : createdAt;

    console.log('Calculated times:', {
      createdAt: createdAt.toISOString(),
      endTime: endTime.toISOString(),
      duration_seconds: extraction.duration_seconds
    });

    const extractionJson = extraction.extraction_json && typeof extraction.extraction_json === 'object'
      ? extraction.extraction_json as Record<string, any>
      : null;

    const pcsPayload = normalizePcsPayload(extraction.pcs)
      || normalizePcsPayload(extraction.extra_json?.pcs)
      || normalizePcsPayload(extractionJson?.pcs)
      || null;

    const ranDiagnostic = typeof extraction.extra_json?.ran_diagnostic === 'boolean'
      ? extraction.extra_json?.ran_diagnostic
      : (typeof extractionJson?.ran_diagnostic === 'boolean' ? extractionJson?.ran_diagnostic : undefined);

    const ranPcs = typeof extraction.extra_json?.ran_pcs === 'boolean'
      ? extraction.extra_json?.ran_pcs
      : (typeof extractionJson?.ran_pcs === 'boolean' ? extractionJson?.ran_pcs : undefined);

    const diagnosticMetadata = extraction.extra_json?.diagnostic_metadata
      || extractionJson?.metadata
      || null;

    const response: ExtractionResponse = {
      request_id: extraction.request_id,
      codes: extraction.codes || [],
      processing_time: extraction.duration_seconds || 0,
      timestamp: createdAt.toISOString(),
      costs: extraction.extra_json?.cost_metrics || undefined,
      metadata: diagnosticMetadata || undefined,
      pcs: pcsPayload || null,
      ran_diagnostic: ranDiagnostic,
      ran_pcs: typeof ranPcs === 'boolean' ? ranPcs : pcsPayload ? true : undefined,
    };

    return {
      id: extraction.request_id,
      status: ExtractionStatus.COMPLETED,
      originalText: extraction.input_text || '',
      request: { text: extraction.input_text || '' },
      progress: [],
      results: response,
      errors: [],
      startTime: createdAt.toISOString(),
      endTime: endTime.toISOString(),
      duration_seconds: extraction.duration_seconds || 0,
      codeColors: this.generateCodeColors(extraction.codes || []),
      title: ExtractionDataTransformer.generateTitle(extraction.input_text || ''),
      feedback: extraction.user_feedback || extraction.extra_json?.feedback || undefined,
    };
  }

  /**
   * Convert multiple saved extractions to sessions
   */
  static savedExtractionsToSessions(extractions: ExtractionResult[]): ExtractionSession[] {
    return extractions.map(extraction => ExtractionDataTransformer.savedExtractionToSession(extraction));
  }

  /**
   * Generate a title from extraction text
   */
  static generateTitle(text: string, maxLength: number = 50): string {
    if (!text) return 'Untitled Extraction';

    const cleaned = text.trim();
    if (cleaned.length <= maxLength) return cleaned;

    // Try to cut at word boundary
    const truncated = cleaned.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');

    if (lastSpace > maxLength * 0.7) {
      return truncated.substring(0, lastSpace) + '...';
    }

    return truncated + '...';
  }
}
